import { Opt } from "@/components/supply/SupplyPickers";
import { money } from "@/components/supply/catalogData";

const F = ({ k, label, v, setC, testId }) => (
  <input className="editorial-input" placeholder={label} value={v.customization[k] || ""} data-testid={testId || `supply-custom-${k}`}
         onChange={(e) => setC(k, e.target.value)} />
);

export const CustomizationFields = ({ v, set, ctx }) => {
  const setC = (k, val) => set({ customization: { ...v.customization, [k]: val } });
  return (
    <div className="space-y-4" data-testid="supply-customization">
      <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-gold)" }}>Customization</div>
      {ctx.birthday && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="supply-birthday-fields">
          <F k="name" label="Child's name" v={v} setC={setC} />
          <F k="age" label="Age being celebrated" v={v} setC={setC} />
          <F k="theme" label="Theme / design direction (describe it)" v={v} setC={setC} />
          <F k="colors" label="Colors" v={v} setC={setC} />
        </div>
      )}
      {ctx.graduation && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="supply-grad-fields">
          {!ctx.birthday && <F k="name" label="Graduate's name" v={v} setC={setC} />}
          <F k="school" label="School" v={v} setC={setC} />
          <F k="grad_year" label="Graduation year" v={v} setC={setC} />
          {!ctx.birthday && <F k="colors" label="Colors" v={v} setC={setC} />}
        </div>
      )}
      {!ctx.birthday && !ctx.graduation && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="supply-general-fields">
          <F k="event_name" label="Name or event to feature" v={v} setC={setC} />
          <F k="theme" label="Theme" v={v} setC={setC} />
          <F k="colors" label="Colors" v={v} setC={setC} />
        </div>
      )}
      <textarea rows={2} className="editorial-input" placeholder="Wording / text to include" value={v.customization.wording || ""}
                onChange={(e) => setC("wording", e.target.value)} data-testid="supply-custom-wording" />
      <textarea rows={2} className="editorial-input" placeholder="Additional design instructions" value={v.customization.instructions || ""}
                onChange={(e) => setC("instructions", e.target.value)} data-testid="supply-custom-instructions" />
      <label className="block text-xs" style={{ color: "var(--ss-muted)" }}>
        Photos, logos and inspiration/reference images (optional, up to 15 files, 15 MB each)
        <input type="file" multiple accept="image/*,application/pdf" className="block mt-2 text-sm" data-testid="supply-files-input"
               onChange={(e) => set({ files: Array.from(e.target.files || []).slice(0, 15) })} />
      </label>
      {v.files.length > 0 && <div className="text-xs" data-testid="supply-files-list">{v.files.map((f) => f.name).join(", ")}</div>}
    </div>
  );
};

export const FulfillmentFields = ({ v, set }) => (
  <div className="space-y-4" data-testid="supply-fulfillment">
    <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-gold)" }}>Pickup, local delivery or shipping</div>
    <div className="flex flex-wrap gap-3">
      <Opt active={v.fulfillment === "pickup"} onClick={() => set({ fulfillment: "pickup" })} testId="book-supply-pickup">Pickup · Free</Opt>
      <Opt active={v.fulfillment === "delivery"} onClick={() => set({ fulfillment: "delivery" })} testId="book-supply-delivery">Local delivery · $10</Opt>
      <Opt active={v.fulfillment === "shipping"} onClick={() => set({ fulfillment: "shipping" })} testId="book-supply-shipping">Shipping · quoted after review</Opt>
    </div>
    {v.fulfillment === "pickup" && (
      <div className="flex flex-wrap gap-3">
        <Opt active={v.pickup_location === "chicago"} onClick={() => set({ pickup_location: "chicago" })} testId="book-supply-pickup-chicago">Chicago pickup</Opt>
        <Opt active={v.pickup_location === "stl"} onClick={() => set({ pickup_location: "stl" })} testId="book-supply-pickup-stl">St. Louis pickup</Opt>
      </div>
    )}
    {v.fulfillment === "delivery" && (
      <input required className="editorial-input" placeholder="Delivery address (street, city, state, ZIP)" data-testid="book-supply-delivery-address"
             value={v.delivery_address} onChange={(e) => set({ delivery_address: e.target.value })} />
    )}
    {v.fulfillment === "shipping" && <>
      <textarea required rows={2} className="editorial-input" placeholder="Complete shipping address (name, street, apt/suite, city, state, ZIP)" data-testid="book-supply-shipping-address"
                value={v.shipping_address} onChange={(e) => set({ shipping_address: e.target.value })} />
      <p className="text-xs" style={{ color: "var(--ss-muted)" }}>Shipping will be calculated after your order is reviewed and added before you approve and pay. Shipping/transit time is in addition to production time; rush shipping is not guaranteed.</p>
    </>}
  </div>
);

export const EstimateBox = ({ est, err, lead }) => (
  <div className="p-4 border text-sm" style={{ borderColor: err || lead ? "var(--ss-rust)" : "var(--ss-border)" }} data-testid="supply-estimate">
    <div className="font-label text-[0.6rem] mb-2" style={{ color: "var(--ss-gold)" }}>Order estimate</div>
    {err && <p data-testid="supply-estimate-error">{err}</p>}
    {!est && !err && <p style={{ color: "var(--ss-muted)" }}>Choose a package or products to see your estimate.</p>}
    {est && <>
      {est.lines.map((l) => (
        <div key={l.key} className="flex justify-between gap-3 py-0.5"><span>{l.label}{l.quantity > 1 && !l.key.includes(":") ? ` × ${l.quantity}` : ""}</span><span>{l.amount == null ? l.note : money(l.amount)}</span></div>
      ))}
      {est.fulfillment && <div className="flex justify-between gap-3 py-0.5"><span>{est.fulfillment.label}</span><span>{est.fulfillment.amount == null ? est.fulfillment.note : est.fulfillment.amount ? money(est.fulfillment.amount) : (est.fulfillment.note || "Free")}</span></div>}
      <div className="flex justify-between font-semibold pt-2 mt-2 border-t" style={{ borderColor: "var(--ss-border)" }}>
        <span>Estimated total{est.needs_review.length ? " (before items priced after review)" : ""}</span><span data-testid="supply-estimate-total">{money(est.estimated_total)}</span>
      </div>
      <p className="text-xs mt-2" style={{ color: "var(--ss-muted)" }} data-testid="supply-lead-time">Minimum lead time: {est.lead_days} calendar days ({est.lead_reason}). Earliest {est.executive_resource ? "event" : "needed-by"} date: {new Date(`${est.earliest_date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}. No rush orders.</p>
      <p className="text-xs mt-1" style={{ color: "var(--ss-muted)" }}>This is an order request, not a confirmed order. Final pricing is approved after review. Includes 2 design revision rounds; additional rounds are $15 each.</p>
    </>}
    {lead && <p className="mt-2" style={{ color: "var(--ss-rust)" }} data-testid="supply-lead-error">{lead}</p>}
  </div>
);
